import React, { useState } from 'react'
import Measure from 'react-measure'
import * as d3 from 'd3'
import { debounce } from 'lodash'
import { useMemo } from 'react'
import { useTheme, makeStyles, alpha } from '@material-ui/core/styles'
import { Box, Popover } from '@material-ui/core'
import useMediaQuery from '@material-ui/core/useMediaQuery'

import { MONTHS, getValueForSegment } from './utils'
import { MonthlyDatum, Segment } from './types'
import TooltipContent from './GraphTooltip'

const useStyles = makeStyles(() => ({
  container: {
    lineHeight: 0,
    height: '100%',
    width: '100%',
    position: 'relative',
  },
  loading: {
    display: 'block',
    position: 'absolute',
    top: '33%',
    left: '33%',
    width: '25%',
    height: '25%',
    margin: 'auto',
  },
}))

const niceTicks = [1, 5, 10, 25, 50, 100, 250, 500, 1000]
const getTicks = (maxY: number): number[] => {
  const tickCount = Math.min(maxY, 5)
  let tickStep = Math.ceil(maxY / tickCount)

  //  If the step is more than 1,000 and is not evenly divisible by 1,000,
  //  then round the step up to the NEXT thousands
  //  ie:
  //      * step 1001 -> 2000
  //      * step 1999 -> 2000
  //      * step 5555 -> 6000
  if (tickStep > 1000) {
    if (tickStep % 1000 !== 0) {
      tickStep = Math.trunc(tickStep / 1000) * 1000 + 1000
    }
  } else {
    //  Otherwise, move up to the next 'nice' step value
    // TODO: random default value
    tickStep = niceTicks.find((value) => value >= tickStep) ?? 1000
  }

  //  Create ticks until we've passed the max value
  const ticks = [0]
  while (ticks[ticks.length - 1] < maxY) {
    ticks.push(tickStep * ticks.length)
  }

  return ticks
}

const formatTick = (tick: number) => {
  if (tick < 5000) return tick.toLocaleString()

  return Math.trunc(tick / 1000) + 'k'
}

type PopoverState<Datum> = {
  data: Datum
  clicked?: boolean
  element: Element
} | null

function StackedBarByMonth<Datum extends MonthlyDatum>({
  label,
  height,
  segments,
  data,
}: {
  label: string
  height: number
  segments: Segment<Datum>[]
  data: Datum[]
}) {
  const [width, setWidth] = useState(0)
  const styles = useStyles()
  const [popoverData, setPopoverData] = useState<PopoverState<Datum>>(null)
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const debounceResize = useMemo(
    () =>
      debounce((newWidth) => {
        setWidth(Math.floor(newWidth))
      }),
    [setWidth],
  )

  const svgPadding = 20 //  The padding between the edge of the SVG and the SVG's content
  const barsPadding = isSmall ? 12 : 22 //  The padding between the first/last bar and the edge of the graph area (ie: for the grid lines)
  const strokeWidth = 8 //  The thickness of the bars
  const circleRadius = strokeWidth / 2 //  The radius of a circle (shown if the bar segment is too small)
  const yAxisWidth = isSmall ? 32 : 78 //  The width of the y axis label area, labels are centered in this area
  const xAxisHeight = 30 //  The height of the x axis label area
  const spacing = 6 //  The spacing between bar segments / circles

  const numDataRecords = data.length

  const xScale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([0, numDataRecords]) //  13 months (last 12 months + current month)
        .range([svgPadding + yAxisWidth + barsPadding, width - svgPadding - barsPadding]),
    [numDataRecords, barsPadding, svgPadding, width, yAxisWidth],
  )
  const maxY = useMemo(() => {
    if (data.length === 0) {
      return 50
    }
    return data.reduce((prev, cur) => {
      const curTotal = segments.reduce((prev, s) => {
        return prev + getValueForSegment(cur, s)
      }, 0)
      return Math.max(prev, curTotal)
    }, 0)
  }, [data, segments])
  const ticks = getTicks(maxY)

  const yScale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([0, ticks[ticks.length - 1]])
        .range([svgPadding + xAxisHeight, height - svgPadding]),
    [ticks, svgPadding, height],
  )
  const safeYScale = (num: number) => yScale(num) ?? 0

  const isTooSmall = (from: number, to: number): boolean => from !== to && to - from <= 1

  const series = useMemo(() => {
    if (data.length === 0 || height === 0) {
      return []
    }

    const stack = d3
      .stack<Datum>()
      .keys(segments.map((_, i) => i.toString()))
      .value((datum, key) => {
        const idx = parseInt(key, 10)
        return getValueForSegment(datum, segments[idx])
      })
    return stack(data)
  }, [data, height, segments])

  return (
    <React.Fragment>
      {popoverData && (
        <Popover
          open={Boolean(popoverData.element)}
          anchorEl={popoverData.element}
          onClose={() => {
            setPopoverData(null)
          }}
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'top',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          style={{
            pointerEvents: popoverData.clicked ? 'auto' : 'none',
          }}
          disableRestoreFocus
        >
          <TooltipContent
            month={MONTHS[popoverData.data.month - 1]}
            chartLabel={label}
            records={segments
              .map((s) => ({
                label: s.displayName,
                value: getValueForSegment(popoverData.data, s),
                color: s.color,
              }))
              .reverse()}
          />
        </Popover>
      )}
      <Measure
        onResize={(contentRect) => {
          if (contentRect && contentRect.entry) {
            debounceResize(contentRect.entry.width)
          }
        }}
      >
        {({ measureRef }) => {
          return (
            <div
              key="measure-ref"
              style={{ height }}
              ref={measureRef}
              className={styles.container}
            >
              <svg
                data-testid="stacked-bar-chart"
                height="100%"
                width="100%"
                viewBox={`0 0 ${width} ${height}`}
                role="img"
                // @ts-expect-error 'alt does not appear on svg'...but it's role="img" so should be good
                alt={`${label} stacked bar chart. For an accessible view, select the View Applications by Month Table button`}
              >
                {ticks.map((tick) => (
                  <React.Fragment key={`tick-${tick}`}>
                    <text
                      data-testid="tick-label"
                      alignmentBaseline="central"
                      fill={alpha(theme.palette.text.primary, 0.7)}
                      textAnchor="end"
                      x={svgPadding + yAxisWidth / 2}
                      y={height - safeYScale(tick)}
                    >
                      {formatTick(tick)}
                    </text>
                    <line
                      stroke={theme.palette.grey[200]}
                      strokeDasharray="4 4"
                      strokeWidth="4"
                      x1={xScale(0) ?? 0 - barsPadding}
                      x2={xScale(data.length) ?? 0 + barsPadding}
                      y1={height - safeYScale(tick)}
                      y2={height - safeYScale(tick)}
                    />
                  </React.Fragment>
                ))}
                {series.map((segmentCategoryByMonth, col) => (
                  <React.Fragment key={col}>
                    {segmentCategoryByMonth.map((_, segCategoryIdx) => {
                      return col === 0 ? (
                        <text
                          key={segCategoryIdx}
                          transform={`translate(${xScale(segCategoryIdx)}, 0)`}
                          fill={alpha(theme.palette.text.primary, 0.7)}
                          textAnchor="middle"
                          y={height - svgPadding / 2}
                        >
                          {MONTHS[data[segCategoryIdx].month - 1]}
                        </text>
                      ) : null
                    })}
                    {segmentCategoryByMonth.map((datum, segCategoryIdx) => {
                      const [from, to] = datum
                      const data = datum.data
                      if (from === to) {
                        return null
                      }
                      const isLastNonEmpty = !series.find(
                        (s, i) => i > col && s[segCategoryIdx][0] !== s[segCategoryIdx][1],
                      )
                      const y1 = safeYScale(from) + spacing
                      const y2 = safeYScale(to) - (isLastNonEmpty ? 0 : spacing)

                      return (
                        <g
                          key={segCategoryIdx}
                          transform={`translate(${xScale(segCategoryIdx)}, ${0})`}
                          onClick={(evt) => {
                            setPopoverData({
                              data: data,
                              clicked: true,
                              element: evt.currentTarget,
                            })
                          }}
                          onMouseOver={(evt) => {
                            if (!popoverData?.clicked) {
                              setPopoverData({
                                data: data,
                                element: evt.currentTarget,
                              })
                            }
                          }}
                          onMouseLeave={() => {
                            if (!popoverData?.clicked) {
                              setPopoverData(null)
                            }
                          }}
                        >
                          {isTooSmall(y1, y2) ? (
                            <circle
                              data-segment-name={segments[col].displayName}
                              data-center-point={(from + to) / 2}
                              cx={0}
                              cy={height - safeYScale((from + to) / 2)}
                              r={circleRadius}
                              fill={segments[col].color}
                            />
                          ) : (
                            <line
                              data-segment-name={segments[col].displayName}
                              data-from={from}
                              data-to={to}
                              x1={0}
                              x2={0}
                              y1={height - y1}
                              y2={height - y2}
                              stroke={segments[col].color}
                              strokeLinecap="round"
                              strokeWidth={strokeWidth}
                            />
                          )}
                        </g>
                      )
                    })}
                  </React.Fragment>
                ))}
              </svg>
            </div>
          )
        }}
      </Measure>
      <Box
        py={2}
        display="flex"
        justifyContent="center"
        flexWrap="row"
      >
        {segments.map((s, i) => (
          <Box
            key={i}
            flexGrow="1"
            textAlign="center"
            aria-label={`Legend for ${s.displayName}`}
          >
            <Box
              borderRadius="50%"
              display="inline-block"
              mr={1}
              width={10}
              height={10}
              style={{ backgroundColor: s.color }}
            />
            {s.displayName}
          </Box>
        ))}
      </Box>
    </React.Fragment>
  )
}

export default StackedBarByMonth
